import { Injectable, inject, DOCUMENT } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { CookieWrapper } from './cookie.service';
import { AuthService } from './auth.service';

export interface UserPreferences {
  boldText: boolean;
  uppercaseText: boolean;
  dyslexicMode: boolean;
  language: string; // 'fr' | 'en' | ...
}

const DEFAULT_PREFERENCES: UserPreferences = {
  boldText: false,
  uppercaseText: false,
  dyslexicMode: false,
  language: 'fr',
};

@Injectable({ providedIn: 'root' })
export class PreferencesService {
  private cookie = inject(CookieWrapper);
  private auth = inject(AuthService);
  private document = inject(DOCUMENT);

  // Observable pour suivre les préférences courantes
  private prefsSubject = new BehaviorSubject<UserPreferences>(
    DEFAULT_PREFERENCES
  );
  public prefs$ = this.prefsSubject.asObservable();

  // Préfixe de cookie. On stocke les préférences par utilisateur : prefs_{userId}
  private COOKIE_PREFIX = 'prefs_';

  constructor() {
    // Lorsque l'utilisateur change (connexion/déconnexion), recharger les préférences
    this.auth.user$.subscribe(() => {
      this.loadForCurrentUser();
    });

    // Au démarrage, tenter de charger les préférences si l'utilisateur est déjà présent
    this.loadForCurrentUser();
  }

  private getCookieNameForCurrentUser(): string {
    const user = this.auth.getCurrentUser();
    const userId = user && user.id ? String(user.id) : 'anonymous';
    return `${this.COOKIE_PREFIX}${userId}`;
  }

  private loadForCurrentUser(): void {
    const cookieName = this.getCookieNameForCurrentUser();
    const raw = this.cookie.get(cookieName);
    if (!raw) {
      // Aucun cookie: conserver les valeurs actuelles ou valeurs par défaut
      const prefs = { ...this.prefsSubject.value };
      this.prefsSubject.next(prefs);
      this.applyClassesToBody(prefs);
      return;
    }

    try {
      const parsed = JSON.parse(
        decodeURIComponent(raw)
      ) as Partial<UserPreferences>;
      const merged: UserPreferences = { ...DEFAULT_PREFERENCES, ...parsed };
      this.prefsSubject.next(merged);
      this.applyClassesToBody(merged);
    } catch (e) {
      console.error('Failed to parse preferences cookie', e);
      const defaultPrefs = { ...DEFAULT_PREFERENCES };
      this.prefsSubject.next(defaultPrefs);
      this.applyClassesToBody(defaultPrefs);
    }
  }

  save(preferences: Partial<UserPreferences>): void {
    const current = {
      ...this.prefsSubject.value,
      ...preferences,
    } as UserPreferences;
    this.prefsSubject.next(current);
    this.applyClassesToBody(current);

    const cookieName = this.getCookieNameForCurrentUser();
    try {
      const toStore = encodeURIComponent(JSON.stringify(current));
      // Stocker 365 jours
      this.cookie.set(cookieName, toStore, 365, '/');
    } catch (e) {
      console.error('Failed to serialize preferences', e);
    }
  }

  resetForCurrentUser(): void {
    const cookieName = this.getCookieNameForCurrentUser();
    this.cookie.delete(cookieName, '/');
    const defaultPrefs = { ...DEFAULT_PREFERENCES };
    this.prefsSubject.next(defaultPrefs);
    this.applyClassesToBody(defaultPrefs);
  }

  getPreferencesSnapshot(): UserPreferences {
    return this.prefsSubject.value;
  }

  private applyClassesToBody(prefs: UserPreferences): void {
    const body = this.document.body;

    // Gérer les classes d'accessibilité
    if (prefs.boldText) {
      body.classList.add('boldText');
    } else {
      body.classList.remove('boldText');
    }

    if (prefs.uppercaseText) {
      body.classList.add('uppercaseText');
    } else {
      body.classList.remove('uppercaseText');
    }

    if (prefs.dyslexicMode) {
      body.classList.add('dyslexicMode');
    } else {
      body.classList.remove('dyslexicMode');
    }
  }
}
